import { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import ClassicLoadingSpinner from '../../../components/LoadingSpinner/ClassicLoadingSpinner';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { useOrderFulfillmentQuery } from '../../../graphql/queries/orderFulfillment.generated';
import { LayoutStoreInstance } from '../../../LayoutStore';
import ContainerStationsTable from './Components/OrderFulfillmentContainerStationsTable';
import {
  IsEventAccountUser,
  IsVytalEmployee,
  ViewerContext,
} from '../../../providers/ViewerContextProvider';
import {
  ContainerTypeAmount,
  OrderFulfillmentType,
} from '../../../graphql/generatedTypes';
import { Communicator } from '../../../tools/Communication';
import OrderFulfillmentManualAmounts from './Components/OrderFulfillmentManualAmounts';
import { ContainerType } from './Components/OrderFulfillmentAddContainerTypes';
import { useArchiveOrderFulfillmentMutation } from '../../../graphql/mutations/archiveOrderFulfillment.generated';
import {
  FaArrowLeft,
  FaCopy,
  FaEnvelope,
  FaLock,
  FaLockOpen,
  FaPlus,
  FaRedo,
  FaTrash,
} from 'react-icons/fa';
import {
  OrderFulfillmentInternalQuery,
  useOrderFulfillmentInternalQuery,
} from '../../../graphql/queries/orderFulfillmentInternal.generated';
import convertDateToDateTimeLocalString from '@/utils/convertDateToDateTimeLocalString';
import { useUpdateOrderFulfillmentMutation } from '../../../graphql/mutations/updateOrderFulfillment.generated';
import { OrderFulfillmentPicturesTable } from './Components/OrderFulfillmentPicturesTable';
import UploadOrderFulfillmentPicturesModal from './Components/UploadOrderFulfillmentPicturesModal';
import { MainHeader } from '../../../components/Layout/MainHeader';
import { ContentSection } from '../../../components/Layout/ContentSection';
import { useSendOrderFulfillmentUpdateEmailMutation } from '../../../graphql/mutations/sendOrderFulfillmentUpdate.generated';
import OrderFulfillmentSingleContainersTable from './Components/OrderFulfillmentSingleContainersTable';
import ChangeHistoryTable from './Components/ChangeHistory/ChangeHistoryTable';
import OrderfulfillmentSummary from './Components/OrderfulfillmentSummary';
import OrderfulfillmentStatusBadge from '../../../components/Orderfulfillment/OrderfulfillmentStatusBadge';
import TrackableItemsTable from './Components/TrackableItemsTable';
import { Routes } from '@/utils/routes';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export const OrderFulfillmentDetailPage = () => {
  const location = useLocation();
  const { fulfillmentId } = useParams<{ fulfillmentId: string }>();

  useDocumentTitle('PageTitles:orderFulfillment', fulfillmentId);
  const viewer = useContext(ViewerContext);
  const isInternal = IsVytalEmployee(viewer);
  const isEventAccount = IsEventAccountUser(viewer);
  const history = useHistory();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editCreatedOn, setEditCreatedOn] = useState(false);
  const [manualAmounts, setManualAmounts] = useState<ContainerTypeAmount[]>([]);
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [orderTimestamp, setOrderTimestamp] = useState(new Date());
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [orderfulfillmentType, setOrderfulfillmentType] = useState('');
  const [newPictureCount, setNewPictureCount] = useState(0);
  const [initialPictureCount, setInitialPictureCount] = useState(0);

  const [updateOrderFulfillment, { loading: updatingOrderFulfillment }] =
    useUpdateOrderFulfillmentMutation({
      refetchQueries: ['OrderFulfillmentInternal'],
    });

  const queryHook = isInternal
    ? useOrderFulfillmentInternalQuery
    : useOrderFulfillmentQuery;

  const { loading, error, data, refetch } = queryHook({
    variables: {
      id: fulfillmentId,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (data) {
      if (data?.orderFulfillment?.manual_amounts.length > 0) {
        setManualAmounts(
          data.orderFulfillment.manual_amounts.map((amount) => ({
            container_type_id: amount.container_type_id,
            container_type_name: amount.container_type_name,
            container_type_image: amount.container_type_image,
            amount: amount.amount + amount.amount_sealed,
            amount_sealed: 0,
          })),
        );
      }
      if (data?.orderFulfillment?.goodwill.length > 0) {
        setManualAmounts(
          data.orderFulfillment.goodwill.map((amount) => ({
            container_type_id: amount.container_type_id,
            container_type_name: amount.container_type_name,
            container_type_image: amount.container_type_image,
            amount: amount.amount,
            amount_sealed: amount.amount_sealed,
          })),
        );
      }

      setOrderfulfillmentType(data.orderFulfillment.type);
    }
  }, [data]);

  const [archiveOrderFulfillment] = useArchiveOrderFulfillmentMutation({
    onCompleted: () => {
      history.push(Routes.events());
      LayoutStoreInstance.setSuccess(i18n.t('OrderFulfillment:archived'));
    },
  });

  const [sendUpdateEmail, { loading: sendingEmail }] =
    useSendOrderFulfillmentUpdateEmailMutation({
      variables: {
        order_fulfillment_id: fulfillmentId,
      },
      onCompleted: () =>
        LayoutStoreInstance.setSuccess(
          i18n.t('OrderFulfillment:sentUpdateEmail'),
        ),
      onError: () =>
        LayoutStoreInstance.setError(
          i18n.t('OrderFulfillment:errorSendingUpdateEmail'),
        ),
    });

  function handleCopy() {
    const timestamp = new Date().getTime();
    localStorage.setItem(
      'copied_order_fulfillment_' + timestamp,
      JSON.stringify({
        type: data?.orderFulfillment?.type,
        event_id: data?.orderFulfillment?.event.id,
        event_account_id: data?.orderFulfillment?.event_account_id,
        item_location: {
          id: data?.orderFulfillment?.item_location.id,
          name: data?.orderFulfillment?.item_location.name,
        },
        manual_amounts: manualAmounts.map((amount) => ({
          container_type_id: amount.container_type_id,
          amount: amount.amount,
          amount_sealed: amount.amount_sealed,
        })),
        container_stations: data?.orderFulfillment?.container_stations.map(
          (station) => ({
            id: station.id,
            name: station.name,
            containers: station.containers.map((container) => ({
              container_id: container.container_id,
              container_type_id: container.container_type_id,
            })),
          }),
        ),
        single_containers: data?.orderFulfillment?.single_containers.map(
          (container) => ({
            container_id: container.container_id,
            container_name: container.container_name,
            container_type_id: container.container_type_id,
          }),
        ),
      }),
    );
    history.push(Routes.createOrderFulfillment(timestamp.toString()));
  }

  const orderFulfillment = data?.orderFulfillment;

  useEffect(() => {
    Communicator.Get('api/3/containers/getAllContainerTypes', {})
      .then((response) => response.json())
      .then((data) => {
        setContainerTypes(data);
      });
  }, []);

  useEffect(() => {
    if (orderFulfillment && orderFulfillment.timestamp) {
      setOrderTimestamp(new Date(orderFulfillment.timestamp));
    }
  }, [orderFulfillment?.timestamp]);

  if (error != null) {
    console.log(error);
    LayoutStoreInstance.setError(i18n.t('OrderFulfillment:errorOnLoad'));
  }

  const internalOrderFulfillment: OrderFulfillmentInternalQuery['orderFulfillment'] =
    data?.orderFulfillment || ({} as any);

  const isDeliveryOFType =
    orderfulfillmentType.toUpperCase() === OrderFulfillmentType.Delivery;

  const currentPictureCount = internalOrderFulfillment.pictures?.length || 0;
  const waitingForNewPictures =
    newPictureCount !== 0 &&
    initialPictureCount + newPictureCount !== currentPictureCount;

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (
      newPictureCount !== 0 &&
      initialPictureCount + newPictureCount !== currentPictureCount
    ) {
      interval = setInterval(() => {
        refetch();
      }, 500);
    } else if (initialPictureCount + newPictureCount === currentPictureCount) {
      setNewPictureCount(0);

      if (interval) {
        clearInterval(interval);
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [newPictureCount, currentPictureCount]);

  return (
    <div>
      <MainHeader title={i18n.t('OrderFulfillment:orderFulfillment')} />

      {loading && <ClassicLoadingSpinner />}
      {orderFulfillment != null && (
        <div>
          <ContentSection title={i18n.t('Common:generalInfo')}>
            {isInternal && (
              <div>
                <Button
                  className="float-end mb-2 ms-2"
                  color="danger"
                  onClick={() => setShowRemoveModal(true)}
                >
                  <FaTrash /> {' ' + i18n.t('Common:delete')}
                </Button>
                <Button className="float-end mb-2 ms-2" onClick={handleCopy}>
                  <FaCopy /> {' ' + i18n.t('OrderFulfillment:copy')}
                </Button>
                <Button
                  className="float-end mb-2 ms-2"
                  onClick={() => sendUpdateEmail()}
                  disabled={sendingEmail}
                >
                  {sendingEmail ? (
                    <ClassicLoadingSpinner />
                  ) : (
                    <>
                      <FaEnvelope />
                      {' ' + i18n.t('OrderFulfillment:sendUpdateEmail')}
                    </>
                  )}
                </Button>
                <Modal
                  isOpen={showRemoveModal}
                  toggle={() => {
                    setShowRemoveModal(false);
                  }}
                >
                  <ModalHeader
                    toggle={() => {
                      setShowRemoveModal(false);
                    }}
                  >
                    {i18n.t('OrderFulfillment:modalTitle')}
                  </ModalHeader>
                  <ModalBody>
                    {i18n.t('OrderFulfillment:modalBody')}: <p></p>
                    {i18n.t('Common:id')}: <p>{fulfillmentId}</p>
                    {i18n.t('OrderFulfillment:type')}:
                    <p>
                      {
                        i18n.t(
                          `OrderFulfillmentType:${orderFulfillment.type?.toUpperCase()}`,
                        ) as string
                      }
                    </p>
                    {i18n.t('OrderFulfillment:event')}:
                    <p>{orderFulfillment.event.name}</p>
                    {i18n.t('OrderFulfillment:itemLocation')}:
                    <p>{orderFulfillment.item_location.name}</p>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      color="danger"
                      onClick={() => {
                        archiveOrderFulfillment({
                          variables: { order_fulfillment_id: fulfillmentId },
                        });

                        setShowRemoveModal(false);
                      }}
                    >
                      {i18n.t('Common:yes')}
                    </Button>{' '}
                    <Button
                      color="secondary"
                      onClick={() => {
                        setShowRemoveModal(false);
                      }}
                    >
                      {i18n.t('Common:no')}
                    </Button>
                  </ModalFooter>
                </Modal>
              </div>
            )}

            {!isInternal && (
              <Link
                to={`/events-dashboard-event/${data?.orderFulfillment.event_account_id}/${data?.orderFulfillment.event.id}/fulfillments`}
                className="float-end mb-2 ms-2"
              >
                <Button>
                  <FaArrowLeft /> {' ' + i18n.t('Common:back')}
                </Button>
              </Link>
            )}

            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderFulfillment:type')}
                </span>
              </div>
              <input
                type="text"
                className="form-control"
                value={
                  i18n.t(
                    `OrderFulfillmentType:${orderFulfillment.type?.toUpperCase()}`,
                  ) as string
                }
                disabled
              />
            </div>

            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderFulfillment:createdOn')}
                </span>
              </div>

              {!editCreatedOn && (
                <>
                  <Input
                    type="text"
                    className="form-control"
                    value={new Date(
                      orderFulfillment.timestamp,
                    ).toLocaleDateString(
                      i18n.language,
                      getDateFormatOptions(true),
                    )}
                    disabled
                  />
                  <Button
                    disabled={updatingOrderFulfillment}
                    onClick={() => {
                      setEditCreatedOn(true);
                    }}
                  >
                    <FaLock />
                    {' ' + i18n.t('Common:edit')}
                  </Button>
                </>
              )}

              {editCreatedOn && (
                <>
                  <Input
                    type="datetime-local"
                    className="form-control"
                    value={convertDateToDateTimeLocalString(orderTimestamp)}
                    onChange={(event) => {
                      setOrderTimestamp(new Date(event.target.value));
                    }}
                  />
                  <Button
                    disabled={updatingOrderFulfillment}
                    color="success"
                    onClick={async () => {
                      await updateOrderFulfillment({
                        variables: {
                          order_fulfillment_id: fulfillmentId,
                          timestamp: orderTimestamp.toISOString(),
                        },
                      });
                      setEditCreatedOn(false);
                    }}
                  >
                    <FaLockOpen />
                    {' ' + i18n.t('Common:save')}
                  </Button>
                  <Button
                    disabled={updatingOrderFulfillment}
                    onClick={() => {
                      setOrderTimestamp(new Date(orderFulfillment.timestamp));
                      setEditCreatedOn(false);
                    }}
                  >
                    {i18n.t('Common:cancel')}
                  </Button>
                </>
              )}
            </div>

            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderFulfillment:createdBy')}
                </span>
              </div>
              {isInternal ? (
                <Link
                  className="form-control"
                  to={'/users/' + orderFulfillment.created_by_id}
                >
                  {orderFulfillment.created_by_name}
                </Link>
              ) : (
                <input
                  className="form-control"
                  type="text"
                  disabled
                  value={orderFulfillment.created_by_name}
                />
              )}
            </div>

            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderFulfillment:status')}
                </span>
              </div>

              <div className="form-control">
                <OrderfulfillmentStatusBadge status={orderFulfillment.status} />
              </div>
            </div>

            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderFulfillment:event')}
                </span>
              </div>

              {isInternal ? (
                <div className="form-control d-flex align-items-center">
                  <Link to={'/event/' + orderFulfillment.event.id}>
                    {orderFulfillment.event.name}
                  </Link>
                  {orderFulfillment.event.is_deleted && (
                    <Badge color="danger" className="ms-2">
                      DELETED
                    </Badge>
                  )}
                </div>
              ) : (
                <div className="form-control d-flex align-items-center">
                  {orderFulfillment.event.name || '-'}
                  {orderFulfillment.event.is_deleted && (
                    <Badge color="danger" className="ms-2">
                      DELETED
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderFulfillment:eventAccount')}
                </span>
              </div>

              {isInternal ? (
                <div className="form-control d-flex align-items-center">
                  <Link
                    to={'/event-account/' + orderFulfillment.event_account_id}
                  >
                    {orderFulfillment.event_account.name}
                  </Link>
                  {orderFulfillment.event_account.is_deleted && (
                    <Badge color="danger" className="ms-2">
                      DELETED
                    </Badge>
                  )}
                </div>
              ) : (
                <div className="form-control d-flex align-items-center">
                  {orderFulfillment.event_account.name || '-'}
                  {orderFulfillment.event_account.is_deleted && (
                    <Badge color="danger" className="ms-2">
                      DELETED
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderFulfillment:itemLocation')}
                </span>
              </div>
              {isInternal ? (
                <Link
                  className="form-control"
                  to={'/item-location/' + orderFulfillment.item_location.id}
                >
                  {orderFulfillment.item_location.name}
                </Link>
              ) : (
                <input
                  className="form-control"
                  type="text"
                  disabled
                  value={orderFulfillment.item_location.name || '-'}
                />
              )}
            </div>

            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('OrderFulfillment:signedBy')}
                </span>
              </div>
              {isInternal && orderFulfillment.authorized_signer_id != null ? (
                <Link
                  className="form-control"
                  to={
                    '/authorized-signer/' +
                    orderFulfillment.authorized_signer_id
                  }
                >
                  {orderFulfillment.authorized_signer_name}
                </Link>
              ) : (
                <input
                  className="form-control"
                  type="text"
                  disabled
                  value={orderFulfillment.authorized_signer_name || '-'}
                />
              )}
            </div>

            <div className="input-group mb-3">
              <span className="input-group-text">{i18n.t('Common:id')}</span>
              <input
                type="text"
                className="form-control"
                value={orderFulfillment.id}
                disabled
              />

              <Button
                onClick={() => {
                  navigator.clipboard.writeText(orderFulfillment.id);
                  LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                }}
              >
                <FaCopy />
              </Button>
            </div>
          </ContentSection>

          <ContentSection title={i18n.t('OrderFulfillment:summary')}>
            <OrderfulfillmentSummary
              orderFulfillment={orderFulfillment}
              containerTypes={containerTypes}
            />
          </ContentSection>

          <ContentSection title={i18n.t('OrderFulfillment:containerStations')}>
            <ContainerStationsTable
              orderFulfillment={orderFulfillment}
              refetchOrderfulfillment={refetch}
              allowDelete
              loading={loading}
            />
          </ContentSection>

          {(orderFulfillment.assets.length > 0 || isInternal) && (
            <ContentSection title={i18n.t('OrderFulfillment:assets')}>
              <TrackableItemsTable
                orderFulfillmentId={fulfillmentId}
                trackableItems={orderFulfillment.assets}
                refetchOrderFulfillment={refetch}
                allowDelete={isInternal}
                allowAdd={isInternal}
              />
            </ContentSection>
          )}

          {orderFulfillment.single_containers.length > 0 && (
            <ContentSection title={i18n.t('OrderFulfillment:singleContainers')}>
              <OrderFulfillmentSingleContainersTable
                orderFulfillment={orderFulfillment}
              />
            </ContentSection>
          )}

          <ContentSection title={i18n.t('OrderFulfillment:manualAmounts')}>
            <OrderFulfillmentManualAmounts
              fulfillmentId={fulfillmentId}
              isInternal={isInternal}
              manualAmounts={manualAmounts}
              containerTypes={containerTypes}
              editModeAlwaysOn={false}
              setManualAmounts={setManualAmounts}
              refetch={refetch}
              type={orderFulfillment.type.toUpperCase() as OrderFulfillmentType}
            />
          </ContentSection>

          {(isInternal || isEventAccount) && (
            <ContentSection title={i18n.t('Common:pictures')}>
              {isInternal && (
                <div className="d-flex justify-content-end">
                  <Button
                    className="mb-1 ms-1"
                    disabled={loading}
                    onClick={() => {
                      refetch();
                    }}
                  >
                    <FaRedo />
                  </Button>
                  <Button
                    className="mb-1 ms-1"
                    onClick={() => setShowUploadModal(true)}
                  >
                    <FaPlus className="me-1" />{' '}
                    {i18n.t('OrderFulfillmentPictures:upload')}
                  </Button>
                </div>
              )}

              <OrderFulfillmentPicturesTable
                error={error}
                loading={loading || waitingForNewPictures}
                pictures={orderFulfillment.pictures || []}
                total={(orderFulfillment.pictures || []).length}
                page={1}
                showLinkButton={false}
                showUnlinkButton={isEventAccount ? false : true}
                showDeleteButton={isEventAccount ? false : true}
                hideCheckbox={isEventAccount}
              />
            </ContentSection>
          )}

          {isInternal && (
            <ContentSection
              title={i18n.t('OrderFulfillment:changeHistory')}
              locked
            >
              <ChangeHistoryTable
                changeHistory={internalOrderFulfillment.change_history}
                isDeliveryOFType={isDeliveryOFType}
                containerTypes={containerTypes}
                orderFulfillment={orderFulfillment}
                isInternal={isInternal}
              />
            </ContentSection>
          )}

          {isInternal && (
            <UploadOrderFulfillmentPicturesModal
              orderFulfillmentId={fulfillmentId}
              isOpen={showUploadModal}
              setIsOpen={(newValue) => setShowUploadModal(newValue)}
              onUploadComplete={(pictureCount) => {
                setNewPictureCount(pictureCount);
                setInitialPictureCount(currentPictureCount);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};
